import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import SimproLogoBlue from 'icons/simpro-logo-blue.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CircleQuestion from 'components/application/CircleQuestion';
import Glimmer from 'components/application/Glimmer';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function SimproIntegrationConfiguration({
  seedSimproJobIntegration,
}) {
  const currentActor = useCurrentActor();

  const [willBeUnlinked, setWillBeUnlinked] = useState(false);

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projects.simproIntegrationConfiguration.components.SimproIntegrationConfiguration',
  });

  const { t: i18nTCommon } = useTranslation('common');

  // query function not required, provided initial data, then data never updated
  const { data: domainSimproJobIntegration } = useQuery({
    queryKey: ['simproJobIntegrations', seedSimproJobIntegration.data.id],
    initialData: seedSimproJobIntegration,
    enabled: false,
  });

  const { simproJobId: simproJobIntegrationSimproJobId } =
    domainSimproJobIntegration.data.attributes;

  const simproCompanyIntegrationId =
    domainSimproJobIntegration.data.relationships.simproCompanyIntegration.data
      .id;

  const { data: domainSimproJob, isSuccess: isDomainSimproJobFetchSuccess } =
    useQuery({
      queryKey: [
        'simproCompanyIntegrations',
        simproCompanyIntegrationId,
        'simproJobs',
        simproJobIntegrationSimproJobId,
      ],
      queryFn: async () => {
        const response = await axios.get(
          `/simpro_company_integrations/${simproCompanyIntegrationId}/simpro_jobs/${simproJobIntegrationSimproJobId}`,
        );

        return response.data;
      },
      placeholderData: { data: null, meta: {} },
      enabled: !!simproJobIntegrationSimproJobId && !willBeUnlinked,
      refetchInterval: (data) =>
        !data || ['completed', 'failed'].includes(data.meta.requestStatus) ?
          false
        : 1000,
      refetchIntervalInBackground: true,
    });

  const handleUnlinkClick = () => {
    setWillBeUnlinked(true);
  };

  const isSimproJobFetchRequestCompleted =
    isDomainSimproJobFetchSuccess &&
    domainSimproJob.meta.requestStatus === 'completed';

  const isSimproJobFetchRequestFailed =
    isDomainSimproJobFetchSuccess &&
    domainSimproJob.meta.requestStatus === 'failed';

  return (
    <div className='form-group'>
      <div className='tw-flex'>
        <span className='col-sm-3 tw-pt-[17px] tw-text-right tw-text-m tw-font-medium tw-tracking-auto'>
          {i18nT('label')}
        </span>
        <div className='col-sm-8 tw-flex'>
          <SimproIntegrationCard>
            {!simproJobIntegrationSimproJobId || willBeUnlinked ?
              <>
                <div className='tw-font-semibold tw-text-grey-900'>
                  Simpro Job: {i18nTCommon('shared.notLinked')}
                </div>
                {willBeUnlinked && (
                  <>
                    <input
                      name='project[simpro_job_integration_attributes][id]'
                      type='hidden'
                      value={domainSimproJobIntegration.data.id}
                    />
                    <input
                      name='project[simpro_job_integration_attributes][simpro_job_id]'
                      type='hidden'
                      value=''
                    />
                  </>
                )}
              </>
            : isSimproJobFetchRequestCompleted ?
              <>
                <div className='tw-font-semibold tw-text-grey-900'>
                  {domainSimproJob.data.attributes.name ||
                    `Simpro Job ID: ${simproJobIntegrationSimproJobId}`}
                </div>
                <div className='tw-mt-3 tw-flex tw-gap-2'>
                  <OutlinedButton
                    color='red'
                    onClick={handleUnlinkClick}
                    size='sm'
                  >
                    {i18nTCommon('shared.unlink')}
                  </OutlinedButton>
                </div>
              </>
            : isSimproJobFetchRequestFailed ?
              <>
                <Trans
                  components={{
                    div: (
                      <div className='tw-mb-1 tw-font-semibold tw-text-red-600' />
                    ),
                    p: (
                      <div className='tw-text-s tw-font-normal tw-tracking-wide' />
                    ),
                  }}
                  i18nKey={`projects.simproIntegrationConfiguration.components.SimproIntegrationConfiguration.${(() => {
                    switch (domainSimproJob.meta.errorReason) {
                      case 'not_found':
                        return 'simproJobNotFoundError';
                      case 'unauthorized':
                        return 'simproJobUnauthorizedError';
                      case 'unknown':
                        return 'simproJobUnknownError';
                      default:
                        return null;
                    }
                  })()}`}
                  ns='components'
                />
                <div className='tw-mt-3 tw-flex tw-gap-2'>
                  <OutlinedButton
                    color='red'
                    onClick={handleUnlinkClick}
                    size='sm'
                  >
                    {i18nTCommon('shared.unlink')}
                  </OutlinedButton>
                </div>
              </>
            : <>
                <div className='tw-text-grey-900'>
                  {i18nTCommon('integrations.integrationSetUp', {
                    provider: 'Simpro',
                  })}
                </div>
                <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                  <span>
                    {i18nTCommon('shared.loading...')}
                    <Glimmer />
                  </span>
                </div>
              </>
            }
          </SimproIntegrationCard>
          {currentActor.isAllowedFeature('approval') && (
            <div className='tooltip-parent tw-ml-3 tw-mt-2.5'>
              <CircleQuestion />
              <Tooltip
                placement='top'
                tooltip={i18nT('tooltip')}
                trigger='hover'
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

SimproIntegrationConfiguration.propTypes = {
  seedSimproJobIntegration: PropTypes.shape({
    data: resourceShape('simproJobIntegration'),
  }),
};

function SimproIntegrationCard({ children }) {
  return (
    <div className='fw-360 tw-flex tw-rounded-md tw-border-1 tw-border-solid tw-border-grey-100 tw-p-4'>
      <div className='tw-pr-3'>
        <div className='tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full'>
          <SimproLogoBlue height='40px' width='40px' />
        </div>
      </div>
      <div className='tw-min-w-0'>{children}</div>
    </div>
  );
}

SimproIntegrationCard.propTypes = {
  children: PropTypes.node.isRequired,
};
